import { Alert, ConfigProvider } from "antd";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import apiClient from "../../helpers/apiClient";
import { useUser } from "../../contexts/UserContext/UserContext";
import useCarPricingMode from "../../features/cars/hooks/useCarPricingMode";
import BannerAlertMessage, { BANNER_ALERT_COMPACT_CLASS } from "./BannerAlertMessage";

/**
 * Guides the owner to complete per-car pricing when `pricingMode === 'car'`.
 * Calls `GET /api/car/pricing-readiness-alert`; renders nothing in `category`
 * mode (pricing lives on the category) or when every car has a usable rate.
 *
 * Symmetric counterpart to `CategoryPricingReadinessAlert` (which renders only in
 * `category` mode); the two banners are mutually exclusive at runtime.
 *
 * @param {{ refreshKey?: unknown }} props - change `refreshKey` to force a re-check.
 */
const CarPricingReadinessAlert = ({ refreshKey }) => {
    const { t } = useTranslation("banner");
    const history = useHistory();
    const { state, myCompanyData } = useUser();
    const isCarPricingMode = useCarPricingMode();

    const [showAlert, setShowAlert] = useState(false);

    useEffect(() => {
        if (!state?.isAuthenticated || !myCompanyData) {
            setShowAlert(false);
            return undefined;
        }

        // Readiness only matters when pricing comes from the car.
        if (!isCarPricingMode) {
            setShowAlert(false);
            return undefined;
        }

        let cancelled = false;

        (async () => {
            try {
                const { data } = await apiClient.get("/api/car/pricing-readiness-alert");
                if (cancelled) return;

                setShowAlert(Boolean(data?.data?.showPricingAlert));
            } catch (_error) {
                if (!cancelled) {
                    setShowAlert(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isCarPricingMode, myCompanyData, refreshKey, state?.isAuthenticated]);

    const handleCloseAlert = () => {
        setShowAlert(false);
    };

    const handleNavigateToCars = () => {
        history.push("/cars");
    };

    if (!isCarPricingMode || !showAlert) {
        return null;
    }

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: "#ff9800",
                    colorInfoBg: "#ff9800",
                    colorText: "#000000",
                    colorIcon: "#000000",
                },
            }}>
            <Alert
                className={BANNER_ALERT_COMPACT_CLASS}
                message={
                    <BannerAlertMessage
                        actionLabel={t("carPricingAlert.button", "Go to Cars")}
                        onAction={handleNavigateToCars}>
                        {t("carPricingAlert.message", "Some of your cars are missing pricing details.")}{" "}
                        <span className="font-semibold">
                            {t("carPricingAlert.action", "Please add a rate to each car")}
                        </span>{" "}
                        {t("carPricingAlert.continuation", "so customers can book them.")}
                    </BannerAlertMessage>
                }
                banner
                type="info"
                closable
                afterClose={handleCloseAlert}
                showIcon={false}
            />
        </ConfigProvider>
    );
};

CarPricingReadinessAlert.propTypes = {
    refreshKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
};

export default CarPricingReadinessAlert;
